import { Code, ConnectError } from '@connectrpc/connect';
import { Errno } from '@puppycad/proto';
import { ApiErrnoError } from './ApiErrnoError';
import { resolveError as defaultResolveError, type ResolvedError } from './error-handler';

/**
 * 未处理 Promise rejection 的监听函数。
 */
type UnhandledRejectionListener = (event: PromiseRejectionEvent) => void;

/**
 * API 未处理错误通知层需要的事件目标。
 */
export interface ApiUnhandledRejectionTarget {
  /**
   * 注册未处理 Promise rejection 监听。
   *
   * @param type 事件类型，当前仅处理 unhandledrejection。
   * @param listener 事件监听函数。
   */
  addEventListener(type: 'unhandledrejection', listener: UnhandledRejectionListener): void;

  /**
   * 移除未处理 Promise rejection 监听。
   *
   * @param type 事件类型，当前仅处理 unhandledrejection。
   * @param listener 事件监听函数。
   */
  removeEventListener(type: 'unhandledrejection', listener: UnhandledRejectionListener): void;
}

/**
 * API 未处理错误通知层依赖。
 */
export interface ApiUnhandledErrorNoticeServiceDependencies {
  /**
   * 全局事件目标，浏览器环境通常为 window。
   */
  target?: ApiUnhandledRejectionTarget | null;

  /**
   * 展示错误消息的方法。
   *
   * @param message 已解析为中文的错误文案。
   */
  notifyError(message: string): void;

  /**
   * 将原始错误解析为统一错误结构。
   *
   * @param error 未处理 rejection 携带的原始原因。
   * @returns 标准化错误结构。
   */
  resolveError(error: unknown): ResolvedError;
}

/**
 * 全局 API 未处理错误通知服务。
 *
 * 该服务只接管没有被调用方 catch 的 API Promise rejection。
 * 已经在业务调用处 catch 的错误不会触发 unhandledrejection，因此仍由业务自行决定提示、回滚或静默。
 */
export class ApiUnhandledErrorNoticeService {
  private readonly target?: ApiUnhandledRejectionTarget | null;

  private readonly notifyError: (message: string) => void;

  private readonly resolveError: (error: unknown) => ResolvedError;

  private installed = false;

  /**
   * 创建全局 API 错误通知服务。
   *
   * @param dependencies 服务依赖，便于在测试中注入假的事件目标和通知函数。
   */
  public constructor(dependencies: ApiUnhandledErrorNoticeServiceDependencies) {
    this.target = dependencies.target;
    this.notifyError = dependencies.notifyError;
    this.resolveError = dependencies.resolveError;
  }

  /**
   * 安装未处理 API 错误监听。
   */
  public install(): void {
    if (this.installed || !this.target) {
      return;
    }

    // 全局只安装一次，避免同一个未处理 API 错误被重复提示。
    this.target.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.installed = true;
  }

  /**
   * 卸载未处理 API 错误监听。
   */
  public dispose(): void {
    if (!this.installed || !this.target) {
      return;
    }

    // 保留卸载路径，方便测试与未来微前端/热更新场景释放全局监听。
    this.target.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.installed = false;
  }

  /**
   * 处理未被调用方捕获的 Promise rejection。
   *
   * @param event 浏览器派发的未处理 Promise rejection 事件。
   */
  private readonly handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const originalError = event.reason;
    const resolvedError = this.resolveError(originalError);

    if (!this.shouldNotify(originalError, resolvedError)) {
      return;
    }

    // 不调用 preventDefault，保留浏览器和日志系统对未处理异常的可观测性。
    this.notifyError(resolvedError.message);
  };

  /**
   * 判断未处理错误是否应进入全局 API 通知层。
   *
   * @param originalError 原始错误对象。
   * @param resolvedError 标准化错误结构。
   * @returns 是否需要展示全局错误消息。
   */
  private shouldNotify(originalError: unknown, resolvedError: ResolvedError): boolean {
    if (!(originalError instanceof ConnectError) && !(originalError instanceof ApiErrnoError)) {
      return false;
    }

    // 认证失效已有独立登录态链路处理，避免重复弹出全局 API 错误。
    return resolvedError.code !== Code.Unauthenticated
      && resolvedError.errno !== Errno.ERRNO_UNAUTHENTICATED;
  }
}

/**
 * 创建浏览器 unhandledrejection 事件目标适配器。
 *
 * @returns 浏览器事件目标；非浏览器环境返回 null。
 */
function createBrowserUnhandledRejectionTarget(): ApiUnhandledRejectionTarget | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return {
    addEventListener(type, listener): void {
      window.addEventListener(type, listener);
    },
    removeEventListener(type, listener): void {
      window.removeEventListener(type, listener);
    },
  };
}

/**
 * 全局 API 未处理错误通知服务实例。
 */
export const apiUnhandledErrorNoticeService = new ApiUnhandledErrorNoticeService({
  target: createBrowserUnhandledRejectionTarget(),
  notifyError(message): void {
    window.$message?.error(message);
  },
  resolveError: defaultResolveError,
});
